import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LockSimple, PaperPlaneRight, ShieldCheck, Paperclip, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import BookingCalendar from "../components/BookingCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Portal() {
  const { token } = useParams();
  const [conv, setConv] = useState(null);
  const [error, setError] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [directory, setDirectory] = useState(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const attach = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/portal/${token}/uploads`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
      toast.success(`${file.name} sent to the team.`);
      await load();
    } catch (e) {
      toast.error(e.message || "Upload failed — try a smaller file.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const load = async () => {
    try {
      const res = await fetch(`${API}/portal/${token}`);
      if (!res.ok) throw new Error();
      setConv(await res.json());
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetch(`${API}/portal/${token}/directory`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setDirectory)
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conv?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/portal/${token}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: text }),
      });
      if (!res.ok) throw new Error();
      setBody("");
      await load();
    } catch {
      // keep the draft intact on failure
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="portal-not-found">
        <div className="max-w-md text-center">
          <LockSimple size={40} weight="duotone" className="text-accent mx-auto" />
          <h1 className="font-serif text-3xl text-primary tracking-tight mt-6">This portal link isn't valid</h1>
          <p className="mt-4 text-foreground/60 text-sm leading-relaxed">
            Portal links are private and created at payment. If yours isn't working, reply to any of our emails and we'll sort it out personally.
          </p>
          <Link to="/" data-testid="portal-not-found-home" className="mt-8 inline-block bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-accent transition-colors duration-300">
            Back to The Timeshare Edit
          </Link>
        </div>
      </div>
    );
  }

  const firstName = conv?.client_name ? conv.client_name.split(" ")[0] : null;

  return (
    <div className="min-h-screen bg-background" data-testid="portal-page">
      <header className="border-b border-border bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <p className="font-serif text-xl text-primary tracking-tight">
            The Timeshare <em className="text-accent">Edit</em>
          </p>
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary" data-testid="portal-secure-badge">
            <ShieldCheck size={16} weight="fill" /> Private portal
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight" data-testid="portal-heading">
            {firstName ? `Welcome, ${firstName}` : "Your consultation thread"}
          </h1>
          <p className="mt-2 text-sm text-foreground/60" data-testid="portal-plan">
            {conv ? `${conv.plan} · Private between you and our team` : "Loading your thread…"}
          </p>
        </motion.div>

        <div className="mt-10 bg-card border border-border rounded-sm p-6 md:p-8 min-h-[280px]" data-testid="portal-message-list">
          {conv && conv.messages.length === 0 && (
            <div className="text-center py-12" data-testid="portal-empty">
              <p className="font-serif text-xl text-primary">No messages yet</p>
              <p className="mt-2 text-sm text-foreground/60 max-w-sm mx-auto leading-relaxed">
                Send your first question below — the offer in your hand, the company that called, the fees that worry you. A real person answers.
              </p>
            </div>
          )}
          <div className="space-y-4">
            {conv &&
              conv.messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "client" ? "justify-end" : "justify-start"}`} data-testid={`portal-message-${i}`}>
                  <div
                    className={`max-w-[85%] rounded-sm px-5 py-4 text-sm leading-relaxed ${
                      m.from === "client"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary/70 text-foreground border border-border"
                    }`}
                  >
                    {m.body && <p className="whitespace-pre-wrap">{m.body}</p>}
                    {m.attachment && (
                      <a
                        data-testid={`portal-attachment-${i}`}
                        href={`${API}/files/${m.attachment.path}?token=${token}`}
                        target="_blank"
                        rel="noreferrer"
                        className={`flex items-center gap-2.5 rounded-sm px-4 py-3 text-sm font-semibold border transition-colors duration-300 ${
                          m.from === "client"
                            ? "border-primary-foreground/25 text-primary-foreground hover:border-accent"
                            : "border-border text-foreground hover:border-accent"
                        }`}
                      >
                        <FileText size={18} weight="duotone" className="shrink-0" />
                        {m.attachment.name}
                        <span className="text-[10px] opacity-60">({Math.round(m.attachment.size / 1024)} KB)</span>
                      </a>
                    )}
                    <p className={`mt-2 text-[10px] font-bold uppercase tracking-wider ${m.from === "client" ? "text-primary-foreground/50" : "text-muted-foreground"}`}>
                      {m.from === "client" ? "You" : "The Timeshare Edit"} · {new Date(m.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))}
          </div>
          <div ref={bottomRef} />
        </div>

        <form onSubmit={send} className="mt-6" data-testid="portal-form">
          <textarea
            data-testid="portal-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            placeholder="Write your question here — include names, dates and numbers if you have them…"
            className="w-full bg-card border border-border rounded-sm px-5 py-4 text-sm font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300 resize-none"
          />
          <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-foreground/45 flex items-center gap-2">
              <LockSimple size={13} weight="bold" /> Only you (via this link) and our team can read this thread.
            </p>
            <div className="flex items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.doc,.docx"
                className="hidden"
                data-testid="portal-file-input"
                onChange={(e) => attach(e.target.files?.[0])}
              />
              <button
                type="button"
                data-testid="portal-attach-button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 border border-border text-foreground/70 px-6 py-3.5 rounded-full font-semibold text-sm hover:border-primary hover:text-primary disabled:opacity-50 transition-colors duration-300"
              >
                <Paperclip size={15} weight="bold" />
                {uploading ? "Uploading…" : "Attach a document"}
              </button>
              <button
                data-testid="portal-send-button"
                type="submit"
                disabled={sending || !body.trim()}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300"
              >
                {sending ? "Sending…" : "Send to the team"}
                <PaperPlaneRight size={15} weight="bold" />
              </button>
            </div>
          </div>
        </form>

        {conv && conv.plan_key === "compass_monthly" && <BookingCalendar token={token} />}

        {directory && directory.length > 0 && (
          <div className="mt-12" data-testid="portal-directory">
            <h2 className="font-serif text-2xl text-primary tracking-tight">The directory — names included</h2>
            <p className="mt-2 text-sm text-foreground/60 leading-relaxed">
              The public ratings behind every profile on the site — BBB, Trustpilot and complaint records, checked {directory[0].as_of}. Shared with members only; please keep it to yourself.
            </p>
            <div className="mt-6 space-y-3">
              {directory.map((c) => (
                <details key={c.id} className="group bg-card border border-border rounded-sm px-6 py-5" data-testid={`portal-company-${c.id}`}>
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg text-primary tracking-tight">{c.name}</p>
                      <p className="mt-0.5 text-xs font-semibold text-foreground/50">{c.category} · {c.rating_detail}</p>
                    </div>
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider border rounded-full px-3 py-1 ${c.upfront_fee ? "text-destructive border-destructive/30" : "text-primary border-primary/30"}`}>
                      {c.upfront_fee ? "Upfront fee" : "Low upfront cost"}
                    </span>
                  </summary>
                  <div className="mt-4 pt-4 border-t border-border space-y-3 text-sm">
                    <p className="text-foreground/70"><span className="font-bold text-foreground/90">Track record:</span> {c.record}</p>
                    <p className="text-foreground/70"><span className="font-bold text-foreground/90">Fee model:</span> {c.fee_model}</p>
                    {c.red_flags.length > 0 && (
                      <div>
                        <span className="font-bold text-foreground/90">Red flags:</span>
                        <ul className="mt-1.5 space-y-1">
                          {c.red_flags.map((f, j) => (
                            <li key={j} className="flex gap-2 text-destructive font-medium">
                              <span className="mt-[8px] h-1 w-1 shrink-0 rounded-full bg-destructive" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-foreground/70"><span className="font-bold text-foreground/90">Best for:</span> {c.best_for}</p>
                  </div>
                </details>
              ))}
            </div>
            <p className="mt-5 text-xs text-foreground/45 leading-relaxed">
              Education, not endorsement — we accept no referral fees, commissions or compensation from any company listed. Ratings change; re-verify before engaging anyone.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
